import React, { useEffect, useState } from 'react';

import Call from '@mui/icons-material/CallRounded';
import PermContactCalendar from '@mui/icons-material/PermContactCalendarRounded';
import School from '@mui/icons-material/SchoolRounded';
import _ from 'lodash';

import DetailsModal, { Fields, Values } from '../../components/DetailsModal';
import PageContent from '../../components/PageContent';
import Tabs from '../../components/Tabs';
import UsersList from '../../components/UsersList';
import { useGetLeadsQuery, useNewLeadMutation } from '../../services/leads';
import { LeadType } from '../../types/common';
import { joinNames } from '../../utils/common/formatText';
import useSearchStr from '../../utils/react/hooks/useSearchStr';
import useSuccess from '../../utils/react/hooks/useSuccess';
import useTab from '../../utils/react/hooks/useTab';

const INITIAL_NEW_FORM = {
  firstName: '',
  lastName: '',
  gradYear: '',
  school: '',
  email: '',
  phoneNumber: '',
  birthday: '',
  parentFirstName: '',
  parentLastName: '',
  parentEmail: '',
  parentPhoneNumber: '',
  streetAddress: '',
  country: '',
  postcode: '',
};

const NEW_FORM_FIELDS: Fields = {
  firstName: {
    editable: true,
    type: 'text',
  },
  lastName: {
    editable: true,
    type: 'text',
  },
  gradYear: {
    editable: true,
    type: 'number',
  },
  school: {
    editable: true,
    type: 'text',
  },
  email: {
    editable: true,
    type: 'text',
  },
  phoneNumber: {
    editable: true,
    type: 'text',
  },
  birthday: {
    editable: true,
    type: 'date',
  },
  parentFirstName: {
    editable: true,
    type: 'text',
  },
  parentLastName: {
    editable: true,
    type: 'text',
  },
  parentEmail: {
    editable: true,
    type: 'text',
  },
  parentPhoneNumber: {
    editable: true,
    type: 'text',
  },
  streetAddress: {
    editable: true,
    type: 'text',
  },
  country: {
    editable: true,
    type: 'text',
  },
  postcode: {
    editable: true,
    type: 'text',
  },
};

const TAB_ALL = 'all';
const TAB_OPTIONS = [
  {
    key: TAB_ALL,
    label: 'All',
  },
  {
    key: LeadType.Enrolment,
    label: 'Enrolments',
    icon: <PermContactCalendar />,
  },
  {
    key: LeadType.Contact,
    label: 'Enquiries',
    icon: <Call />,
  },
];

const AdminEnrolmentsPage = () => {
  /**
   * Tab Control
   */
  const [tab, setTab] = useTab({ default: LeadType.Enrolment, key: 'type' });

  const [searchStr, setSearchStr] = useSearchStr('');

  /**
   * Leads Queries & Mutations
   */
  const { data: leads, isLoading: isLeadsLoading } = useGetLeadsQuery({
    ...(tab === TAB_ALL ? {} : { type: tab }),
    ...(searchStr ? { search: searchStr } : {}),
  });

  /**
   * Displayed Leads Generation
   */
  const [displayedLeads, setDisplayedLeads] = useState<any[]>([]);

  useEffect(() => {
    if (!_.includes(Object.values(TAB_OPTIONS).map((opt) => opt.key), tab)) {
      setTab();
    } else {
      setTab(tab);
    }

    setDisplayedLeads(leads?.data || []);
  }, [tab, leads]);

  /**
   * Entry Form
   */
  const [newLeadFormOpen, setNewLeadFormOpen] = useState<boolean>(false);
  const [newLeadForm, setNewLeadForm] = useState({});
  const [newLead,
    {
      isSuccess: isNewLeadSuccess,
      isLoading: isNewLeadLoading,
      error: newLeadError,
      reset: resetNewLead,
    }] = useNewLeadMutation();

  const resetNewLeadFormFields = () => {
    setNewLeadForm(INITIAL_NEW_FORM);
  };

  useEffect(() => {
    // Close new lead form on success
    if (isNewLeadSuccess) setNewLeadFormOpen(false);
  }, [isNewLeadSuccess]);

  useSuccess(isNewLeadSuccess, 'Lead created');

  const resetMutationStates = async () => {
    resetNewLead();
    resetNewLeadFormFields();
  };

  /**
   * Entry Form Functions
   */

  const onNewLeadCreate = (values?: Values) => {
    if (values) {
      newLead({
        // Remove object attributes with value of empty string
        attributes: Object.fromEntries(Object.entries(values).filter(([, value]) => value !== '')),
      });
    }
  };

  return (
    <PageContent>
      <Tabs
        options={TAB_OPTIONS}
        selectedKey={tab}
        onChange={(key) => {
          setTab(key);
        }}
      />
      <UsersList
        columns={[
          ...(tab === TAB_ALL ? [{
            field: 'type',
            headerName: 'Type',
            width: 25,
            renderCell: (params: any) => {
              if (params.value === LeadType.Contact) return <Call color="darkPrimary" />;
              if (params.value === LeadType.Enrolment) return <PermContactCalendar color="darkPrimary" />;
              return <School color="darkPrimary" />;
            },
          }] : []),
          {
            field: 'gradYear',
            headerName: 'Grad Year',
            width: 100,
          },
          {
            field: 'school',
            headerName: 'School',
            width: 200,
          },
          {
            field: 'name',
            headerName: 'Name',
            width: 200,
          },
          {
            field: 'parentName',
            headerName: 'Parent Name',
            width: 200,
            renderCell: (params: any) =>
              joinNames([params.row.parentFirstName, params.row.parentLastName]),
          },
          {
            field: 'phoneNumber',
            headerName: 'Phone Number',
            width: 200,
          },
        ]}
        data={displayedLeads || []}
        loading={isLeadsLoading}
        onSearch={(s) => setSearchStr(s)}
        getRowHref={(row: any) => `/admin/lead/${row?.type}/${row?.leadId}`}
        {...((tab === TAB_ALL || tab === LeadType.Contact) ? {
          onAddClick: () => {
            resetMutationStates();
            setNewLeadFormOpen(true);
          },
          addButtonLabel: 'Add Enquiry',
        } : { })}
        {...(tab === LeadType.Enrolment ? {
          onAddClick: () => window.open(
            'https://cognitotuition.com/enrol',
            '_blank',
          ),
          addButtonLabel: 'Enrol',
        } : {})}
      />
      <DetailsModal
        open={newLeadFormOpen}
        handleClose={() => setNewLeadFormOpen(false)}
        title="Add New Enquiry"
        fields={NEW_FORM_FIELDS}
        values={newLeadForm}
        error={newLeadError}
        buttons={[{
          fn: async (fields?: Fields) =>
            onNewLeadCreate(fields),
          label: 'Add',
          color: 'darkPrimary',
          loading: isNewLeadLoading,
        }]}
        onFieldChange={(key, value) => {
          setNewLeadForm({
            ...newLeadForm,
            [key]: value,
          });
        }}
      />
    </PageContent>
  );
};
export default AdminEnrolmentsPage;
